/*
 * protenders_legacy.js — esbuild IIFE entry
 * ==========================================
 * Replaces the Sprockets pipeline:
 *   - protenders.js (was jQuery2 + Foundation + jQuery plugins + custom app JS)
 *   - foundation_init.js (Foundation 5 initialization w/ jQuery3 compat patches)
 *   - protenders_deferred.js (savvior, simplyCountable)
 *
 * Load order: this bundle MUST load AFTER app_bundle.js which sets
 * window.$ = window.jQuery = npm jQuery 3.7.1.
 *
 * All plugins here receive jQuery via globals — they detect window.jQuery
 * through their own UMD/IIFE footers.
 */

// ─── 1. jQuery + Migrate (already set as global by app_bundle, re-import here
//        ensures esbuild can resolve dependencies in this bundle too) ───────
import $ from 'jquery';
import 'jquery-migrate';

// Expose globals for plugins that check window.jQuery / window.$
window.jQuery = window.$ = $;

// ─── 2. jQuery UI (sortable, autocomplete, button, datepicker) ───────────────
import 'jquery-ui-dist/jquery-ui.min';

// ─── 3. Foundation 5 (all plugins) ──────────────────────────────────────────
import 'foundation-sites/js/foundation/foundation';
import 'foundation-sites/js/foundation/foundation.alert';
import 'foundation-sites/js/foundation/foundation.clearing';
import 'foundation-sites/js/foundation/foundation.dropdown';
import 'foundation-sites/js/foundation/foundation.equalizer';
import 'foundation-sites/js/foundation/foundation.reveal';
import 'foundation-sites/js/foundation/foundation.tab';
import 'foundation-sites/js/foundation/foundation.tooltip';
import 'foundation-sites/js/foundation/foundation.abide';

// ─── 4. Foundation initialization + jQuery 3 compatibility patches ────────────
// Migrated from foundation_init.js (was separate Sprockets script).
// Foundation.global.namespace is forced to '' to prevent font-family CSS
// bleeding into data-attribute selectors (e.g. [data-Lato, sans-serif-reveal]).
(function initFoundation() {
    'use strict';

    function lockNamespace() {
        if (!window.Foundation || !Foundation.global) { return; }
        // Force namespace to empty string. Use Object.defineProperty to prevent
        // Foundation.set_namespace() from overwriting it with the CSS font-family value.
        try {
            Object.defineProperty(Foundation.global, 'namespace', {
                get: function () { return ''; },
                set: function () { },  // silently ignore all writes
                configurable: true
            });
        } catch (e) {
            // Fallback if defineProperty is not available
            Foundation.global.namespace = '';
        }
    }

    function patchReveal() {
        var reveal = Foundation && Foundation.libs && Foundation.libs.reveal;
        if (!reveal) { return; }

        // jQuery3: animation callbacks on hidden elements may not fire.
        // 'none' resets locked=false immediately inside show().
        reveal.settings.animation = 'none';
        reveal.settings.close_on_background_click = false;

        // jQuery3 removed .selector from jQuery objects.
        // Foundation.open() uses typeof target.selector to find the modal.
        // Without it, Foundation falls to else-branch: modal = $(this.scope) = $(document) → crash.
        var _origOpen = reveal.open;
        reveal.open = function (target) {
            try {
                if (target && target.jquery && typeof target.selector === 'undefined') {
                    var revealId = target.data(this.data_attr('reveal-id'));
                    target.selector = revealId ? '#' + revealId : '';
                }
                return _origOpen.apply(this, arguments);
            } catch (e) {
                this.locked = false;
            }
        };
    }

    function init() {
        if (typeof Foundation === 'undefined') { return; }
        var jQ = window.jQuery || $;

        // Register the reveal modal click handler FIRST — outside try/catch so it
        // always fires even if Foundation init or patchReveal() throws below.
        // Capture phase (true) fires before Foundation's bubble-phase handler.
        document.addEventListener('click', function (e) {
            var target = e.target.closest('a[data-reveal-id]');
            if (!target) { return; }
            e.preventDefault();
            e.stopPropagation();
            var targetId = target.getAttribute('data-reveal-id');
            if (targetId) {
                try {
                    if (window.Foundation && Foundation.libs.reveal) {
                        Foundation.libs.reveal.locked = false;
                    }
                    (window.jQuery || $)('#' + targetId).foundation('reveal', 'open');
                } catch (err) {}
            }
        }, true); // true = capture phase — runs before Foundation's broken bubble-phase handler

        try {
            lockNamespace();

            // Foundation 5 + jQuery3: $(window).load(fn) removed in jQuery3.
            var _$fnLoad = jQ.fn.load;
            jQ.fn.load = function (url, params, callback) {
                if (typeof url === 'function') { return this.on('load', url); }
                return _$fnLoad ? _$fnLoad.apply(this, arguments) : this;
            };

            jQ(document).foundation();

            // Post-init jQuery3 compat patches
            patchReveal();

        } catch (e) {
            if (Foundation && Foundation.libs && Foundation.libs.reveal) {
                Foundation.libs.reveal.locked = false;
            }
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

// ─── 5. jQuery plugins (npm) ─────────────────────────────────────────────────
import 'jquery.cookie';
import 'select2';
import 'bxslider/dist/jquery.bxslider';
import 'slick-carousel';
import 'fotorama/fotorama';
import 'parsleyjs';
import 'jquery-powertip';
import scrollToFixed from 'scrolltofixed';  // no-op if not jQuery plugin style

// Numeral (number formatting)
import numeral from 'numeral';
window.numeral = numeral;

// Underscore (template/utility)
import _ from 'underscore';
window._ = _;

// Cloudinary v2.x removed CloudinaryJQuery — provide a safe no-op stub.
// The cloudinary_js_config Rails helper emits: $.cloudinary.config({cloud_name, ...})
// companies_grid_view.js calls: $.cloudinary.responsive()
// Both just need to not crash. Image URLs are generated server-side via cloudinary_url().
// Running the actual responsive() method (which tracks viewport/resizes images by URL)
// caused unintended layout side-effects when called with an empty cloud_name.
var _cloudinaryStub = { config: function() {}, responsive: function() {} };
$.cloudinary = _cloudinaryStub;
if (window.$) { window.$.cloudinary = _cloudinaryStub; }
if (window.jQuery) { window.jQuery.cloudinary = _cloudinaryStub; }

// Lazysizes (image lazy loading — self-initializing)
import 'lazysizes';

// Dropzone (file uploads)
import Dropzone from 'dropzone';
window.Dropzone = Dropzone;

// URI.js
import URI from 'urijs';
window.URI = URI;

// jQuery sparkline
import 'jquery-sparkline';

import './vendor/stay.js';

// ─── 6. Vendor-only plugins (no npm equivalent — copied from Sprockets) ───────
import './vendor/jquery.qtip.js';
import './vendor/jquery.sticky.js';
import './vendor/jquery.daterangepicker.js';
// import './vendor/caesar.min.js'; // REMOVED — ROT13 decoding moved to decode_link_controller.js
import './vendor/jquery.glossarize.js';
import './vendor/locationpicker.jquery.js';
import './vendor/nested_form.js';

// Rail UJS — start if not already started by Sprockets rails-ujs or turbo
if (typeof Rails !== 'undefined' && !window._railsUJSStarted) {
    try { Rails.start(); window._railsUJSStarted = true; } catch (e) { }
}

// ─── 8. Custom app JS (migrated from app/assets/javascripts) ─────────────────
// These files use bare $ globals — they work because jQuery is set above.
import './legacy/global.js';
import './legacy/advanced_search.js';
import './legacy/foundation_namespace_fix.js';
import './legacy/companies.js';
import './legacy/company_document.js';
import './legacy/employments.js';
import './modules/protenders/custom-taggables.js'; // defines make_taggable — must come before company_projects.js
import './legacy/company_projects.js';
import './legacy/company_widgets.js';
import './legacy/company_profile.js';
import './legacy/locations.js';
import './legacy/responsive.js';
import './legacy/items_slider.js';
import './legacy/products.js';
import './legacy/product_requests.js';
import './legacy/product_sets.js';
import './legacy/projects.js';
import './legacy/participation.js';
import './legacy/sidebar_utils.js';
import './legacy/navigation_toggler.js';
import './legacy/recurly_func.js';
import './legacy/team_page.js';
import './legacy/seal.js';

// ─── 9. Deferred plugins (was protenders_deferred.js) ────────────────────────
// savvior UMD uses `this` as root — which is undefined inside esbuild's IIFE (strict mode).
// Import as a named default so esbuild resolves it via CJS and we can set window.savvior.
import savviorLib from './vendor/savvior.js';
window.savvior = savviorLib;
import './vendor/jquery.simplyCountable.js';

// ─── 10. Globals ─────────────────────────────────────────────────────────────
window.root = window; // Rails RJS namespace
